import {
  Blockquote,
  BulletList,
  Card,
  Figure,
  Heading,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@generalapplications/ui";
import { ArticleLinkCard } from "@/app/features/blog/components/ArticleLinkCard";
import { CodeViewer } from "./CodeViewer";
import { ResponsiveImage } from "@/app/common/components/composite/media/ResponsiveImage";
import type { RichContentBlock } from "@/content/contentSchema";
import { formatText } from "@/app/common/functions/format/formatText";
import { sectionIds } from "@/app/features/content/functions/articleSections";

// The renderer for the shared block vocabulary (contentSchema.ts). Shared
// between blog articles and service pages because they draw the same shapes —
// not because they are the same kind of content. Each caller supplies its own
// figure registry, so a diagram authored for one never has to be reachable
// from the other.

export type FigureComponentRegistry = Record<string, React.ComponentType>;

export type RichContentProps = {
  blocks: RichContentBlock[];
  figureComponents?: FigureComponentRegistry;
};

export function RichContent({
  blocks,
  figureComponents = {},
}: RichContentProps) {
  // Each H2 carries an id, so a table of contents and a link can reach it.
  const ids = sectionIds(blocks);
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.type) {
          case "paragraph":
            return (
              <Text key={index} variant="prose">
                {formatText(block.text)}
              </Text>
            );
          case "heading":
            return block.level === 2 ? (
              <Heading key={index} level={2} size="xl" id={ids.get(index)}>
                {block.text}
              </Heading>
            ) : (
              <Heading key={index} level={3} size="lg">
                {block.text}
              </Heading>
            );
          case "code":
            return <CodeViewer key={index} block={block} />;
          case "table":
            return (
              <Table key={index} caption={block.caption}>
                <TableHeader>
                  <TableRow>
                    {block.columns.map((column) => (
                      <TableHead key={column} scope="col">
                        {column}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {block.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex} verticalAlign="start">
                          {formatText(cell)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          case "list":
            return (
              <Text key={index} as="div" variant="prose">
                <BulletList>
                  {block.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{formatText(item)}</li>
                  ))}
                </BulletList>
              </Text>
            );
          case "quote":
            return (
              <Blockquote key={index} size="lg">
                <Stack gap="xs">
                  <Text variant="inherit">{formatText(block.text)}</Text>
                  {block.author && (
                    <Text variant="caption">&mdash; {block.author}</Text>
                  )}
                </Stack>
              </Blockquote>
            );
          case "figure": {
            const figureId =
              "id" in block && typeof block.id === "string"
                ? block.id
                : undefined;

            if (figureId?.startsWith("article-link:")) {
              const slug = figureId.replace("article-link:", "");
              return (
                <ArticleLinkCard
                  key={index}
                  slug={slug}
                  label={block.caption || undefined}
                />
              );
            }

            const FigureComponent = figureId
              ? figureComponents[figureId]
              : null;
            const imgSrc =
              "src" in block && typeof block.src === "string"
                ? block.src
                : undefined;

            return (
              <Figure
                key={index}
                caption={block.caption || undefined}
                credit={("credit" in block && block.credit) || undefined}
              >
                <Card variant="soft" tone="neutral" radius="xl" padding="sm">
                  {FigureComponent ? (
                    <FigureComponent />
                  ) : imgSrc ? (
                    <ResponsiveImage
                      src={imgSrc}
                      alt={block.alt}
                      width={1200}
                    />
                  ) : (
                    import.meta.env.DEV && (
                      <Text variant="error">
                        Error: Unknown figure ID &quot;{figureId}&quot;
                      </Text>
                    )
                  )}
                </Card>
              </Figure>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}
